import React, { useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, Typography } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { FCM_DEVICE_TOKEN_LS_KEY } from "src/constants";
import { AUTH_ROUTES } from "src/pages/auth/constants";
import { clearTableState } from "src/utils/tableStateStorage";

import { AuthenticationService, NotificationService } from "../../services";

export function LogoutConfirmModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const fcmToken = localStorage.getItem(FCM_DEVICE_TOKEN_LS_KEY);
      if (fcmToken) {
        try {
          await NotificationService.unregisterDevice(fcmToken);
        } catch (e) {
          console.warn("[Logout] Failed to unregister device token:", e);
        }
      }
      await AuthenticationService.logout();
    } finally {
      localStorage.removeItem(FCM_DEVICE_TOKEN_LS_KEY);
      clearTableState();
      dispatch({ type: "CLEAR_STORE" });
      onClose?.();
      navigate(AUTH_ROUTES.login, { replace: true });
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(103,75,245,0.18)",
        },
      }}
    >
      {/* Brand gradient top bar */}
      <Box sx={{ height: 5, background: "linear-gradient(90deg, #674BF5 0%, #a78bfa 100%)" }} />

      <DialogContent sx={{ textAlign: "center", px: 4, pt: 4, pb: 3.5 }}>
        {/* Animated icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            background: "linear-gradient(135deg, #674BF5 0%, #a78bfa 100%)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            boxShadow: "0 12px 32px rgba(103,75,245,0.35)",
          }}
        >
          <LogoutIcon sx={{ color: "white", fontSize: 32 }} />
        </Box>

        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: "#1a1a2e",
            mb: 0.75,
            letterSpacing: "-0.3px",
          }}
        >
          Leaving so soon?
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.7,
            mb: 1,
          }}
        >
          You&apos;ll be securely signed out of your session.
        </Typography>

        {/* We'll miss you chip */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            background: "rgba(103,75,245,0.07)",
            color: "#674BF5",
            borderRadius: 10,
            px: 1.5,
            py: 0.5,
            mb: 3,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <SentimentSatisfiedAltIcon sx={{ fontSize: 15 }} />
          We&apos;ll be right here when you&apos;re back!
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              py: 1.15,
              fontSize: 14,
              borderColor: "#e0e0e0",
              color: "#555",
              "&:hover": {
                borderColor: "#674BF5",
                color: "#674BF5",
                background: "rgba(103,75,245,0.04)",
              },
            }}
          >
            Stay
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirm}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.8)" }} />
              ) : (
                <LogoutIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              py: 1.15,
              fontSize: 14,
              background: "linear-gradient(135deg, #674BF5 0%, #a78bfa 100%)",
              boxShadow: "0 6px 20px rgba(103,75,245,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a3fe0 0%, #9370f0 100%)",
                boxShadow: "0 8px 24px rgba(103,75,245,0.5)",
              },
              "&.Mui-disabled": {
                background: "#d4d4d4",
                boxShadow: "none",
              },
            }}
          >
            {loading ? "Signing out…" : "Yes, Sign Out"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
